import { Modal, Input, notification } from "antd";
import React, { useState, useEffect } from "react";
import { Button } from "../Button";
import CopyBlackIcon from "../../icons/copy-icon-black.svg";
import DownloadBlackIcon from "../../icons/download-black.svg";
import SecurityService from "@services/Security.service";
import { use2FAFieldLogic } from "../../hooks/use2FAFieldLogic";
import { copyToClipboard, downloadBackupCodes } from "../../utils";
import { useMediaQuery } from "react-responsive";
import { DESKTOP_WIDTH } from "@bundles/consts";

type TwoFaData = {
  qrCode?: string;
  secretCode?: string;
};

type TwoFAModalProps = {
  open?: boolean;
  onClose?: () => void;
  fetch2FAStatus: () => void;
};

export const TwoFAModal = ({
  open,
  onClose,
  fetch2FAStatus,
}: TwoFAModalProps) => {
  const [twoFaData, setTwoFaData] = useState<TwoFaData | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(true);
  const [backupCodes, setBackupCodes] = useState([]);

  const isMobile = useMediaQuery({ maxWidth: DESKTOP_WIDTH });

  const {
    inputRefs,
    pin,
    handleChange,
    handleKeyDown,
    handleClearPin,
    handlePaste,
  } = use2FAFieldLogic();

  const handleOnSave = (customPin?: string) => {
    SecurityService.twoFaConfirm({
      code_2fa: customPin ?? pin.join(""),
    })
      .then((resp) => {
        setBackupCodes(resp?.data?.backup_codes ?? []);
        setIsConfirmed(true);
        fetch2FAStatus();
      })
      .catch((err) => {
        if (Array.isArray(err.response.data)) {
          notification.error({
            message: err.response.data[0],
          });
        }

        handleClearPin();
      });
  };

  const handleCopySecretCode = () => {
    copyToClipboard({
      text: twoFaData?.secretCode,
      successMessage: "Secret code copied to clipboard",
      errMessage: "Failed to copy secret code",
    });
  };

  const handleCopyBackupCodes = () => {
    copyToClipboard({
      text: backupCodes.join(", "),
      successMessage: "Backup codes copied to clipboard",
      errMessage: "Failed to copy backup codes",
    });
  };

  const handleDownlaodBackupCodes = () => {
    downloadBackupCodes(backupCodes);
    notification.success({
      message: "Backup codes downloanded.",
    });
  };

  const handleTwoFAEnterKeyPress = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    handleKeyDown(index, e);

    if (e.key.match(/^[0-9]$/)) {
      const updatedPin = [...pin];
      updatedPin[index] = e.key;

      if (updatedPin.join("").length === 6) {
        handleOnSave(updatedPin.join(""));
      }
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    handlePaste(e, (pastedText) => {
      handleOnSave(pastedText);
    });
  };

  useEffect(() => {
    if (open) {
      SecurityService.twoFaSetup().then((resp) => {
        setTwoFaData({
          qrCode: resp?.data?.qr_code,
          secretCode: resp?.data?.secret_key,
        });
      });
    }

    if (!open) {
      handleClearPin();
      setTwoFaData(null);
      setIsConfirmed(false);
      setBackupCodes([]);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={isMobile ? "100%" : "70%"}
      className="tailwind-2fa-modal"
    >
      <div className="p-6">
        {!isConfirmed ? (
          <>
            <h2 className="text-xl font-semibold">Instruction for setup</h2>
            <ol className="list-decimal pl-5 mt-4 text-gray-700">
              <li>
                <strong>Download an authentication app</strong>
                <p>
                  We recommend Authy or Google Authenticator available on most
                  App stores if you don’t have any installed yet.
                </p>
              </li>
              <li className="mt-2">
                <strong>Scan the QR code or copy the key</strong>
                <p>
                  Tap 'Scan QR code' on Authy or 'Scan a barcode' on Google
                  Authenticator and scan the QR code.
                </p>
                <div className="flex flex-col lg:flex-row items-stretch gap-8">
                  <div className="flex-1 flex items-center justify-center rounded-[20px] border border-grey-lighter p-[25px]">
                    {twoFaData?.qrCode && (
                      <img
                        className="max-w-full max-h-full object-contain"
                        src={`data:image/svg+xml;base64,${btoa(
                          twoFaData?.qrCode
                        )}`}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-h-full rounded-[20px] border border-grey-lighter p-[25px] flex flex-col gap-4 items-center justify-center">
                    <div className="text-black text-[16px] max-w-[180px]">
                      {twoFaData?.secretCode}
                    </div>
                    <Button onClick={handleCopySecretCode}>Copy</Button>
                  </div>
                </div>
              </li>
              <li className="mt-2">
                <strong>
                  Copy and enter 6-digit PIN from your Authenticaton App
                </strong>
                <p>
                  After the QR code is scanned or the key is entered, your
                  authentication app will generate a 6-digit PIN. Copy the PIN
                  below and submit it.
                </p>
              </li>
            </ol>
            <div className="mt-4">
              <div className="font-semibold">Enter 6-digit PIN</div>
              <div className="flex gap-2 mt-2">
                {pin.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    maxLength={1}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleTwoFAEnterKeyPress(index, e)}
                    onPaste={handleOnPaste}
                    className="text-center text-lg font-semibold border border-grey-lighter tailwind-2fa-input"
                  />
                ))}
              </div>
            </div>

            <div className="mt-[40px]">
              <Button
                isPrimary
                onClick={() => handleOnSave()}
                className="w-full"
              >
                Submit
              </Button>
            </div>
          </>
        ) : (
          <div>
            <h2 className="text-xl font-semibold">
              Two factor authentication is On
            </h2>

            <div className="mt-2">
              This is the same process you will need to follow every time you
              login to your account once 2FA is enabled.
            </div>

            <div className="mt-4">
              <div className="font-semibold mb-2">Backup codes for 2FA</div>

              <div>
                If you lose access to your authentication app device, you can
                use one of these backup codes to login to your account. Each
                code may be used only once.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              {backupCodes.map((code) => (
                <div className="bg-grey-light text-center rounded-[10px] p-4 text-[16px]">
                  {code}
                </div>
              ))}
            </div>

            <Button
              onClick={handleDownlaodBackupCodes}
              className="mt-8 w-full flex items-center gap-2"
            >
              Download <DownloadBlackIcon />
            </Button>

            <Button
              onClick={handleCopyBackupCodes}
              className="mt-2 w-full flex items-center gap-2"
            >
              Copy <CopyBlackIcon />
            </Button>

            <Button isPrimary onClick={onClose} className="mt-[40px] w-full">
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
